import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Edit2, Info, X, Copy } from 'react-feather'
import { OrderInfo, formatPrice, getOrderStatusText } from '../../hooks/useOrderBook'
import { OrderType, OrderStatus, USDT_DECIMALS, MUD_DECIMALS } from '../../constants/orderbook'
import { shortenAddress, getEtherscanLink } from '../../utils'
import { useActiveWeb3React } from '../../hooks'
import { ExternalLink } from '../../theme/components'
import { ChainId } from '@uniswap/sdk'
import Popover from '../../components/Popover'
import styled from 'styled-components'
import {
  OrderCardWrapper,
  OrderCardHeader,
  CardHeaderRight,
  CardIconBtn,
  OrderTypeBadge,
  StatusBadge,
  ProgressBar,
  ProgressFill,
  InfoRow,
  InfoLabel,
  InfoValue
} from './styleds'

const AddressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
`
const AddressLink = styled(ExternalLink)`
  font-size: 12px;
  font-weight: 500;
`
const CopyBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.text2};
  flex-shrink: 0;
  &:hover {
    background: ${({ theme }) => theme.bg3};
    color: ${({ theme }) => theme.text1};
  }
`
const CopyPopoverContent = styled.div`
  padding: 6px 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.text1};
  white-space: nowrap;
`

interface OrderCardProps {
  order: OrderInfo
  onCancel: (orderId: number) => void
  onUpdatePrice: (orderId: number, currentPrice: string) => void
  onDetail: (orderId: number, isBuy?: boolean) => void
  cancellingId: number | null
  /** 是否显示用户地址（截断、可复制、可展开） */
  showAddress?: boolean
  /** 是否显示编辑、取消按钮（管理员所有订单列表不显示） */
  showEditCancel?: boolean
}

export default function OrderCard({ order, onCancel, onUpdatePrice, onDetail, cancellingId, showAddress, showEditCancel = true }: OrderCardProps) {
  const { t } = useTranslation()
  const { chainId } = useActiveWeb3React()
  const [addressCopied, setAddressCopied] = useState(false)
  const isBuy = order.type === OrderType.BUY
  const isActive = order.status === OrderStatus.OPEN

  // Calculate progress
  const filled = parseFloat(order.filled)
  const total = parseFloat(order.amount)
  const percent = total > 0 ? (filled / total) * 100 : 0

  // 最多 2 位小数，去掉末尾 0：1500.00→1500，1550.80→1550.8
  const toMax2Trim = (n: number): string => {
    if (n === 0) return '0'
    const s = n.toFixed(2)
    return s.replace(/\.?0+$/, '') || '0'
  }

  // 最多 4 位小数，去掉末尾 0（挂单价/成交均价用）
  const toMax4Trim = (n: number): string => {
    if (n === 0) return '0'
    const s = n.toFixed(4)
    return s.replace(/\.?0+$/, '') || '0'
  }

  // 已成交/总量 数值（买入为 USDT，卖出为 MUD）
  const filledHuman = isBuy ? filled / Math.pow(10, USDT_DECIMALS) : filled / Math.pow(10, MUD_DECIMALS)
  const totalHuman = isBuy ? total / Math.pow(10, USDT_DECIMALS) : total / Math.pow(10, MUD_DECIMALS)
  const unit = isBuy ? 'USDT' : 'MUD'
  const filledTotalDisplay = `${toMax2Trim(filledHuman)}/${toMax2Trim(totalHuman)}(${percent.toFixed(1)}%)`

  const orderPriceHuman = parseFloat(order.price) / Math.pow(10, USDT_DECIMALS)
  const feeFactor = 1 - (order.feeRate || 0) / 1000
  const expectedHuman = orderPriceHuman > 0
    ? isBuy
      ? (filledHuman * feeFactor) / orderPriceHuman
      : filledHuman * feeFactor * orderPriceHuman
    : 0
  const receivedHuman = isBuy
    ? parseFloat(order.totalReceived) / Math.pow(10, MUD_DECIMALS)
    : parseFloat(order.totalReceived) / Math.pow(10, USDT_DECIMALS)
  const receivedUnit = isBuy ? 'MUD' : 'USDT'
  const expectedReceivedDisplay = `${toMax2Trim(expectedHuman)}/${toMax2Trim(receivedHuman)}(${receivedUnit})`

  // 成交均价 USDT/MUD（扣费后）：买入 = (已付 USDT - 手续费) / 获得 MUD，卖出 = 获得 USDT / (已卖 MUD - 手续费)
  const filledRaw = parseFloat(order.filled)
  const receivedRaw = parseFloat(order.totalReceived)
  const feeRaw = parseFloat(order.totalFee)
  const filledAfterFee = filledRaw - feeRaw
  const avgPrice =
    receivedRaw > 0 && filledAfterFee > 0
      ? isBuy
        ? (filledRaw - feeRaw) / Math.pow(10, USDT_DECIMALS) / (receivedRaw / Math.pow(10, MUD_DECIMALS))
        : (receivedRaw / Math.pow(10, USDT_DECIMALS)) / ((filledRaw - feeRaw) / Math.pow(10, MUD_DECIMALS))
      : 0

  // 挂单价/成交均价 一行，保留最多 4 位小数并去尾 0
  const orderPriceNum = parseFloat(order.price) / Math.pow(10, USDT_DECIMALS)
  const orderPriceStr = toMax4Trim(orderPriceNum)
  const avgPriceStr = avgPrice > 0 ? toMax4Trim(avgPrice) : '--'
  const priceAvgPriceDisplay = `${orderPriceStr}/${avgPriceStr}`

  // Format time
  const createdTime = new Date(order.createdAt).toLocaleString()

  return (
    <OrderCardWrapper>
      <OrderCardHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <OrderTypeBadge isBuy={isBuy}>
            {isBuy ? t('orderBuy') : t('orderSell')}
          </OrderTypeBadge>
        </div>
        <CardHeaderRight>
          <StatusBadge status={order.status}>{t(getOrderStatusText(order.status))}</StatusBadge>
          {showEditCancel && isActive && (
            <>
              <CardIconBtn
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  onUpdatePrice(order.orderId, formatPrice(order.price))
                }}
                title={t('orderUpdatePrice')}
                aria-label={t('orderUpdatePrice')}
              >
                <Edit2 size={14} />
              </CardIconBtn>
              <CardIconBtn
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  onCancel(order.orderId)
                }}
                disabled={cancellingId === order.orderId}
                title={cancellingId === order.orderId ? t('orderCancelling') : t('orderCancel')}
                aria-label={t('orderCancel')}
              >
                <X size={14} />
              </CardIconBtn>
            </>
          )}
          <CardIconBtn
            type="button"
            onClick={e => {
              e.stopPropagation()
              onDetail(order.orderId, isBuy)
            }}
            title={t('orderDetail')}
            aria-label={t('orderDetail')}
          >
            <Info size={14} />
          </CardIconBtn>
        </CardHeaderRight>
      </OrderCardHeader>

      {/* Progress */}
      {order.status === OrderStatus.OPEN && (
        <ProgressBar>
          <ProgressFill percent={percent} />
        </ProgressBar>
      )}

      {/* 地址（管理员所有订单列表用）：同详情里哈希 - 可跳转、Popover 复制 */}
      {showAddress && order.user && (
        <InfoRow>
          <InfoLabel>{t('orderAdminOrderAddress')}</InfoLabel>
          <InfoValue>
            <AddressRow>
              {chainId != null ? (
                <AddressLink href={getEtherscanLink(chainId as ChainId, order.user, 'address')} target="_blank" rel="noopener noreferrer">
                  {shortenAddress(order.user)}
                </AddressLink>
              ) : (
                <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{shortenAddress(order.user)}</span>
              )}
              <Popover show={addressCopied} content={<CopyPopoverContent>{t('orderCopySuccess')}</CopyPopoverContent>} placement="top">
                <CopyBtn
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(order.user).then(() => {
                      setAddressCopied(true)
                      setTimeout(() => setAddressCopied(false), 1500)
                    })
                  }}
                  title={t('orderCopyTxHash')}
                >
                  <Copy size={14} />
                </CopyBtn>
              </Popover>
            </AddressRow>
          </InfoValue>
        </InfoRow>
      )}

      {/* Details：已成交/总量(unit)  555.72/1500(37%) */}
      <InfoRow>
        <InfoLabel>{t('orderFilledTotal', { unit })}</InfoLabel>
        <InfoValue>{filledTotalDisplay}</InfoValue>
      </InfoRow>

      {/* 手续费率 */}
      <InfoRow>
        <InfoLabel>{t('orderFeeRate')}</InfoLabel>
        <InfoValue>{(order.feeRate / 10).toFixed(1)}%</InfoValue>
      </InfoRow>

      {/* 挂单价/成交均价  x.xx/y.yy 或 x.xx/--，同 toMax2Trim */}
      <InfoRow>
        <InfoLabel>{t('orderPriceAvgPrice')}</InfoLabel>
        <InfoValue>{priceAvgPriceDisplay}</InfoValue>
      </InfoRow>

      {parseFloat(order.totalReceived) > 0 && (
        <InfoRow>
          <InfoLabel>{t('orderExpectedReceived')}</InfoLabel>
          <InfoValue>{expectedReceivedDisplay}</InfoValue>
        </InfoRow>
      )}

      <InfoRow>
        <InfoLabel>{t('orderCreatedAt')}</InfoLabel>
        <InfoValue style={{ fontSize: 11, opacity: 0.7 }}>{createdTime}</InfoValue>
      </InfoRow>
    </OrderCardWrapper>
  )
}
